/**
 * The shape of a formula reference, shared by both disciplines.
 *
 * At the `views/v2/` root because `FormulasPanelV2` renders either discipline's list and
 * both lists need the same types. Held in `accounting/formulas.ts` before, which made the
 * panel import across the boundary the moment it learned about valuation — and
 * `boundary.test.ts` said so, which is the whole reason that test exists.
 *
 * The same move as `settingsFields.tsx`: what two disciplines share goes up, not sideways.
 */

export type TabKey =
  | 'cogs'
  | 'staffing'
  | 'indirect'
  | 'fixed-assets'
  | 'pnl'
  | 'control'
  | 'cashflow'
  | 'balance-sheet'
  | 'equity'
  // The six below are SCHEDULES rather than tabs: nothing shows them on their own,
  // and each decides a line on the balance sheet or the P&L. They are documented
  // because a figure decided somewhere the reader cannot see is exactly what the
  // formulas panel is for.
  | 'debt'
  | 'deferred-tax'
  | 'leases'
  | 'provisions'
  | 'pensions'
  | 'impairment';

export interface FormulaEntry {
  /** The computed line, named as the sheet names it. */
  label: string;
  /** How it is calculated, in the sheet's own terms. */
  formula: string;
  /**
   * Tabs this line reads from. Empty when everything it needs is local.
   *
   * Always ACCOUNTING tabs, in both lists. The dependency runs one way: a valuation
   * reads finished statements, and nothing in the accounts reads a valuation.
   */
  sources?: TabKey[];
  /** Anything a reader would otherwise get wrong. */
  note?: string;
  /**
   * Sheets this line reads, by name, for a list whose sheets are not IAS accounting's
   * tabs - Baseline Accounting's are the source workbook's own.
   */
  sourceLabels?: string[];
  /** The documented fix that replaced this line's formula, when one did. */
  fix?: { id: string; title: string };
}

export interface FormulaSection {
  section: string;
  entries: FormulaEntry[];
}

export const TAB_LABELS: Record<TabKey, string> = {
  cogs: 'COGS',
  staffing: 'Staffing',
  indirect: 'Indirect Costs',
  'fixed-assets': 'Fixed Assets & Depn',
  pnl: 'P&L',
  control: 'Control Account',
  cashflow: 'Cashflow',
  'balance-sheet': 'Balance Sheet',
  equity: 'Changes in Equity',
  debt: 'Debt schedule',
  'deferred-tax': 'Deferred tax',
  leases: 'Leases',
  provisions: 'Provisions',
  pensions: 'Pensions (IAS 19)',
  impairment: 'Loss allowance (IFRS 9)',
};
